import { InfoOutlined } from '@mui/icons-material';
import {
    Box,
    FormControl,
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import { ChangeEvent, useEffect } from 'react';
import { getTimezone } from 'utils';
import { setScheduledEndTimestamp } from 'domains/campaigns/components/CustomCampaign/customCampaignActions';
import { getEndDateTimeError, minDate } from 'domains/campaigns/utils';
import DisabledTooltipContent from '../../../../components/DisabledTooltipContent';

export type Props = {
    dispatchCampaignAction: React.Dispatch<any>;
    endDate: DateTime;
    hasEndDate: boolean;
    sendDate: DateTime;
    setEndDate: (payload: DateTime) => void;
    setHasEndDate: (payload: boolean) => void;
    setIsContinueEnabled: (isContinueEnabled: boolean) => void;
    setIsRecommendedTimeSwitchTouched: (payload: boolean) => void;
    disabled?: boolean;
};

const EndDateTimePicker = ({
    dispatchCampaignAction,
    endDate,
    hasEndDate,
    sendDate,
    setEndDate,
    setHasEndDate,
    setIsContinueEnabled,
    setIsRecommendedTimeSwitchTouched,
    disabled = false,
}: Props) => {
    const onChangeHasEndDate = (event: ChangeEvent<HTMLInputElement>) => {
        if ((event.target as HTMLInputElement).value === 'true') {
            setHasEndDate(true);
            setEndDate(sendDate);
            // Dispatch the initial end date when "Ends On" is selected
            if (sendDate?.isValid) {
                dispatchCampaignAction(setScheduledEndTimestamp(sendDate.toISO()));
            }
        } else {
            dispatchCampaignAction(setScheduledEndTimestamp(''));
            setHasEndDate(false);
            setEndDate(null);
        }
        setIsRecommendedTimeSwitchTouched(true);
    };

    const onChangeEndDatetime = (datetime: DateTime) => {
        setEndDate(datetime);

        if (datetime?.isValid) {
            const newDatetime = datetime.set({ year: datetime.year, month: datetime.month, day: datetime.day });
            dispatchCampaignAction(setScheduledEndTimestamp(newDatetime.toISO()));
            setEndDate(newDatetime);
        }

        setIsRecommendedTimeSwitchTouched(true);
    };

    const endDateErrorMessage = getEndDateTimeError(endDate, sendDate);
    const hasEndDateError = hasEndDate && endDate;
    const shouldShowEndDateErrorMessage = hasEndDateError && Boolean(endDateErrorMessage);

    useEffect(() => {
        setIsContinueEnabled(!shouldShowEndDateErrorMessage);
    }, [setIsContinueEnabled, shouldShowEndDateErrorMessage]);

    return (
        <>
            <Tooltip title={disabled ? <DisabledTooltipContent /> : ''} arrow followCursor>
                <FormControl disabled={disabled}>
                    <Stack
                        direction="row"
                        columnGap={2}
                        alignItems="center"
                        my={-2}
                        sx={disabled ? { opacity: 0.6 } : {}}
                    >
                        <FormLabel>
                            <Typography variant="body2">End Date/Time</Typography>
                        </FormLabel>
                        <RadioGroup
                            value={hasEndDate}
                            onChange={onChangeHasEndDate}
                            defaultValue={false}
                            name="end-date-group"
                            row
                        >
                            <FormControlLabel
                                value={false}
                                control={<Radio size="small" color="secondary" disabled={disabled} />}
                                label={<Typography variant="body2">Never</Typography>}
                                disabled={disabled}
                            />
                            <FormControlLabel
                                value={true}
                                control={<Radio size="small" color="secondary" disabled={disabled} />}
                                label={<Typography variant="body2">Ends On</Typography>}
                                data-testid="recurrence-end-date-label"
                                disabled={disabled}
                            />
                        </RadioGroup>
                    </Stack>
                </FormControl>
            </Tooltip>
            {hasEndDate && (
                <Tooltip title={disabled ? <DisabledTooltipContent /> : ''} arrow followCursor>
                    <Box>
                        <DateTimePicker
                            data-testid="send-date-picker"
                            disabled={disabled}
                            label={
                                <>
                                    End Date/Time ({getTimezone()})
                                    <Tooltip title="This is the last day that any sends will go out.">
                                        <InfoOutlined style={{ fontSize: '14px', margin: '0 0 -2px 6px' }} />
                                    </Tooltip>
                                </>
                            }
                            minDateTime={minDate()}
                            slotProps={{
                                textField: {
                                    error: shouldShowEndDateErrorMessage,
                                    helperText: hasEndDateError && endDateErrorMessage,
                                    id: 'end-datetime-picker',
                                },
                                popper: {
                                    placement: 'bottom-end',
                                },
                            }}
                            value={endDate}
                            onChange={(datetime) => onChangeEndDatetime(datetime)}
                        />
                    </Box>
                </Tooltip>
            )}
        </>
    );
};

export default EndDateTimePicker;
